import { apiRequest } from '../client';
import { getApiToken } from '../authToken';
import { toJsonSafe } from '../json';

export async function listPackages() {
  const result = await apiRequest('/api/packages');
  return result.packages || [];
}

export async function getPackage(packageId) {
  const result = await apiRequest(`/api/packages/${encodeURIComponent(packageId)}`);
  return result.package || null;
}

export async function getCompanySubscription({ companyId, token }) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/subscription`,
    { token: authToken }
  );
  return result;
}

export async function patchCompanySubscription({ companyId, subscription, token }) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/subscription`,
    {
      token: authToken,
      method: 'PATCH',
      body: { subscription: toJsonSafe(subscription) },
    }
  );
  return result.subscription || null;
}

export async function upsertGatewaySubscription({ companyId, subscriptionId, subscription, token }) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
    {
      token: authToken,
      method: 'PUT',
      body: { subscription: toJsonSafe(subscription) },
    }
  );
  return result.subscription || null;
}

export async function listBillingHistory({ companyId, limit = 10, token } = {}) {
  const authToken = token || (await getApiToken());
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/billing-history?${params.toString()}`,
    { token: authToken }
  );
  return result.history || [];
}

function wrapGatewayError(error) {
  const body = error?.body || {};
  const wrapped = new Error(body.error || error.message || 'Subscription request failed');
  wrapped.status = error.status;
  wrapped.body = body;
  wrapped.code = body.code || error.code;
  wrapped.details = { code: body.code, ...(body.details && typeof body.details === 'object' ? body.details : {}), ...(typeof body.details === 'string' ? { message: body.details } : {}) };
  throw wrapped;
}

async function gatewayRequest(path, { method = 'POST', body, token } = {}) {
  try {
    return await apiRequest(path, { token, method, body: toJsonSafe(body) });
  } catch (error) {
    wrapGatewayError(error);
  }
}

export async function createGatewaySubscription(payload, token) {
  return gatewayRequest('/api/subscriptions/deluxe/create', { body: payload, token });
}

export async function getGatewaySubscription({ subscriptionId, deluxeEnvironment, authorizeEnvironment, token }) {
  const authToken = token || (await getApiToken());
  return gatewayRequest('/api/subscriptions/deluxe/get', {
    body: { subscriptionId, deluxeEnvironment, authorizeEnvironment },
    token: authToken,
  });
}

export async function cancelGatewaySubscription({ subscriptionId, reason, deluxeEnvironment, authorizeEnvironment, token }) {
  const authToken = token || (await getApiToken());
  return gatewayRequest('/api/subscriptions/deluxe/cancel', {
    body: { subscriptionId, reason, deluxeEnvironment, authorizeEnvironment },
    token: authToken,
  });
}

export async function changeGatewayBillingCycle(payload, token) {
  const authToken = token || (await getApiToken());
  return gatewayRequest('/api/subscriptions/deluxe/change-billing-cycle', {
    body: payload,
    token: authToken,
  });
}

export async function createBillingHistoryRecord({ companyId, billing, token }) {
  const authToken = token || (await getApiToken());
  const result = await apiRequest(
    `/api/companies/${encodeURIComponent(companyId)}/billing-history`,
    {
      token: authToken,
      method: 'POST',
      body: { billing: toJsonSafe(billing) },
    }
  );
  return result.id;
}
